/**
 * Browser-only preference: after sign-in (or empty hash), land on Command Center.
 * Default = map-first (large fleet map). Overview = workbench desks expanded.
 * Both stay on #/overview — no separate immersive shell.
 */

import { canUseOpsMap } from "./ops-map-policy.js";

const LS_KEY = "croc.home.landing.v1";

/** @returns {"overview" | "map"} */
export function getHomeLandingPreference() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === "map" || v === "overview") return v;
  } catch (_) {}
  // Product default: map is the hero on login (Apple-style surround chrome).
  return "map";
}

/** @param {"overview" | "map"} mode */
export function setHomeLandingPreference(mode) {
  const m = mode === "map" ? "map" : "overview";
  try {
    localStorage.setItem(LS_KEY, m);
  } catch (_) {}
  return m;
}

/**
 * Sync body + CC root for map-first vs workbench layout (no overlay on the map canvas).
 * @param {ParentNode | null | undefined} root
 * @param {unknown} [me]
 * @returns {"overview" | "map"}
 */
export function applyHomeLandingChrome(root, me) {
  let mode = getHomeLandingPreference();
  if (me != null) {
    try {
      if (mode === "map" && !mapNavAllowed(me)) mode = "overview";
    } catch (_) {
      mode = "overview";
    }
  }
  try {
    document.body.dataset.ccLanding = mode;
  } catch (_) {}
  const host = root && /** @type {HTMLElement} */ (root.querySelector
    ? root.querySelector("#crocCcRoot") || (root.id === "crocCcRoot" ? root : null)
    : null);
  if (host && host.setAttribute) {
    host.setAttribute("data-cc-landing", mode);
  }
  // Overview = desks + side floats open. Map = floating glass, start folded.
  try {
    const wb = (host || document).querySelector("[data-home-wb]");
    if (wb) {
      if (mode === "overview") {
        wb.setAttribute("data-wb-open", "1");
        const btn = wb.querySelector("[data-wb-toggle]");
        if (btn) {
          btn.setAttribute("aria-expanded", "true");
          btn.textContent = "Hide";
        }
      } else {
        // Map big-screen: always start folded so pins stay clickable.
        // Sticky "open" pref only applies after the operator taps Workflow.
        let dockPref = null;
        try { dockPref = localStorage.getItem("croc_home_wb_dock"); } catch (_) {}
        const docked = dockPref == null ? true : dockPref === "1";
        wb.setAttribute("data-wb-open", "0");
        wb.setAttribute("data-wb-dock", docked ? "1" : "0");
        const btn = wb.querySelector("[data-wb-toggle]");
        if (btn) {
          btn.setAttribute("aria-expanded", "false");
          btn.textContent = "Workflow";
        }
        const dockBtn = wb.querySelector("[data-wb-dock-toggle]");
        if (dockBtn) {
          dockBtn.setAttribute("aria-expanded", docked ? "false" : "true");
          dockBtn.textContent = docked ? "Open" : "Fold";
        }
      }
    }
    const root = host || document;
    ["devices", "feed"].forEach((key) => {
      const panel = root.querySelector(`[data-cc-float="${key}"]`);
      if (!panel) return;
      let pref = null;
      try { pref = localStorage.getItem(key === "devices" ? "croc_home_float_devices" : "croc_home_float_feed"); } catch (_) {}
      const open = mode === "overview" ? pref !== "0" : pref === "1";
      panel.setAttribute("data-float-open", open ? "1" : "0");
      panel.querySelectorAll(`[data-float-toggle="${key}"], [data-float-tab="${key}"]`).forEach((el) => {
        el.setAttribute("aria-expanded", open ? "true" : "false");
        if (el.matches("[data-float-toggle]")) el.textContent = open ? "Fold" : "Open";
      });
    });
  } catch (_) {}
  return mode;
}

/** @param {unknown} me */
function mapNavAllowed(me) {
  try {
    return !!canUseOpsMap(me);
  } catch (_) {
    return false;
  }
}

/**
 * @param {HTMLElement | null} root
 * @param {{ toast?: (msg: string, kind?: string) => void; me?: unknown }} [opts]
 * @returns {() => void} dispose
 */
export function wireHomeLandingPick(root, opts = {}) {
  if (!root || typeof root.querySelectorAll !== "function") return () => {};
  const me = opts.me;
  const toast = opts.toast;

  const currentLandingFromHash = () => {
    const route = String(location.hash || "").split("?")[0];
    if (route === "#/overview") return getHomeLandingPreference();
    return "overview";
  };

  const sync = () => {
    const cur = currentLandingFromHash();
    const mapOk = mapNavAllowed(me);
    applyHomeLandingChrome(root.closest("#crocCcRoot") || document.getElementById("crocCcRoot") || root, me);
    for (const btn of root.querySelectorAll("[data-croc-landing]")) {
      const v = btn.getAttribute("data-croc-landing");
      const on = (v === "map" || v === "overview") && v === cur;
      btn.setAttribute("aria-pressed", on ? "true" : "false");
      btn.classList.toggle("croc-landing-pick__btn--on", on);
      if (v === "map") {
        btn.disabled = !mapOk;
        btn.setAttribute("aria-disabled", mapOk ? "false" : "true");
        btn.title = mapOk ? "Map" : "Map — not available yet";
      }
    }
    // Nudge MapLibre after layout flip (safe no-op if absent).
    try {
      window.dispatchEvent(new Event("resize"));
    } catch (_) {}
  };

  sync();

  const onHash = () => {
    sync();
  };
  window.addEventListener("hashchange", onHash);

  const onClick = (e) => {
    const t = e.target && e.target.closest && e.target.closest("[data-croc-landing]");
    if (!t || !root.contains(t)) return;
    const v = t.getAttribute("data-croc-landing");
    if (v !== "map" && v !== "overview") return;

    if (v === "map" && !mapNavAllowed(me)) {
      if (typeof toast === "function") {
        toast("Home map is not available for this account.", "warn");
      }
      return;
    }

    const prevPref = getHomeLandingPreference();
    setHomeLandingPreference(v);

    /** Both modes stay in Command Center — map is the center glass panel. */
    const target = "#/overview";
    const here = String(location.hash || "").split("?")[0];
    if (here !== target) {
      location.hash = target;
    } else {
      sync();
      if (v === "map") {
        try {
          const stage = document.querySelector("#cc-overview-map-canvas, .croc-cc-map-stage");
          if (stage && typeof stage.scrollIntoView === "function") {
            stage.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch (_) {}
      }
    }

    if (typeof toast === "function" && prevPref !== v) {
      toast(
        v === "map"
          ? "Home shows the map first"
          : "Home shows the response workbench first",
        "ok",
      );
    }
  };
  root.addEventListener("click", onClick);
  return () => {
    try {
      root.removeEventListener("click", onClick);
    } catch (_) {}
    try {
      window.removeEventListener("hashchange", onHash);
    } catch (_) {}
  };
}
